import {
  GraphQLClient,
  type RequestOptions,
  type TypedDocumentString,
  type Variables,
} from 'graphql-request';

import { getAnonymousId } from '../shared/trackers';
import { normalizeZeusResult, type ZeusResult } from './zeusResult';

let client: GraphQLClient | undefined;
let partialDataClient: GraphQLClient | undefined;

const createClient = (errorPolicy?: 'all') => {
  if (typeof window === 'undefined') {
    throw new Error('The Cosmos Zeus visitor client requires a browser origin');
  }

  return new GraphQLClient(new URL('/api/zeus', window.location.href).toString(), {
    credentials: 'include',
    ...(errorPolicy ? { errorPolicy } : {}),
    headers: {
      'apollographql-client-name': 'cosmos',
      'apollographql-client-version': process.env.NEXT_PUBLIC_VERSION ?? 'local',
    },
  });
};

const getClient = () => {
  client ??= createClient();
  return client;
};

const getPartialDataClient = () => {
  partialDataClient ??= createClient('all');
  return partialDataClient;
};

export const requestZeusFromBrowser = <TResult, TVariables extends Variables>(
  document: TypedDocumentString<TResult, TVariables>,
  variables: TVariables,
  signal?: AbortSignal,
): Promise<TResult> => {
  const anonymousId = getAnonymousId();
  const options: RequestOptions<Variables, TResult> = {
    document,
    variables,
    ...(anonymousId ? { requestHeaders: { 'x-opendoor-anonymous-id': anonymousId } } : {}),
    ...(signal ? { signal } : {}),
  };

  return getClient().request<TResult>(options);
};

interface ZeusBrowserPartialDataRequestOptions<TResult, TVariables extends Variables> {
  /** Generated operation whose successful fields should remain usable alongside GraphQL errors. */
  document: TypedDocumentString<TResult, TVariables>;
  /** Variables for the generated operation. */
  variables: TVariables;
  /** Optional signal used to cancel the browser request. */
  signal?: AbortSignal;
}

export const requestZeusFromBrowserAllowPartialData = <TResult, TVariables extends Variables>({
  document,
  variables,
  signal,
}: ZeusBrowserPartialDataRequestOptions<TResult, TVariables>): Promise<ZeusResult<TResult>> => {
  const anonymousId = getAnonymousId();

  return getPartialDataClient()
    .rawRequest<TResult>({
      query: document.toString(),
      variables,
      ...(anonymousId ? { requestHeaders: { 'x-opendoor-anonymous-id': anonymousId } } : {}),
      ...(signal ? { signal } : {}),
    })
    .then(normalizeZeusResult);
};
